import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import {
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormHelperText,
    IconButton,
    InputLabel,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import Select from "@mui/material/Select";

import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { bulkLinkUsers, createCustomer, fetchCustomers } from "../../../../../api/customers";
import { useCurrentProfile } from "../../../../../ContextWrapper";
import { mapFrontendToBackend } from "../../../../../utils/accountTypeUtils";
import { getAllowedAccountRoles } from "../../../../../utils/userRoleUtils";

export default function AccountCreateDialog({ onClose, onSubmit, onShowSuccess }) {
  const navigate = useNavigate();
  const { profile } = useCurrentProfile();

  const [formData, setFormData] = useState({
    accountName: "",
    accountType: "",
    primaryContact: "",
    primaryContactEmail: "",
    manager: "",
    bookkeeper: "",
  });

  const [alternativeUsers, setAlternativeUsers] = useState([]);
  const [newUserName, setNewUserName] = useState("");
  const [newUserType, setNewUserType] = useState("");
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [primaryContactNotFound, setPrimaryContactNotFound] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedAlternativeUser, setSelectedAlternativeUser] = useState(null);
  const [accountNameValidating, setAccountNameValidating] = useState(false);
  const [accountNameError, setAccountNameError] = useState("");
  const [accountNameDebounceTimer, setAccountNameDebounceTimer] = useState(null);

  // State for bookkeeper search
  const [bookkeepers, setBookkeepers] = useState([]);
  const [selectedBookkeeper, setSelectedBookkeeper] = useState(null);
  const [loadingBookkeepers, setLoadingBookkeepers] = useState(false);

  // State for manager search
  const [managers, setManagers] = useState([]);
  const [selectedManager, setSelectedManager] = useState(null);
  const [loadingManagers, setLoadingManagers] = useState(false);

  // State for alternative users search
  const [alternativeUsersOptions, setAlternativeUsersOptions] = useState([]);
  const [loadingAlternativeUsers, setLoadingAlternativeUsers] = useState(false);

  // State for controlling autocomplete open/close
  const [primaryContactOpen, setPrimaryContactOpen] = useState(false);
  const [managerOpen, setManagerOpen] = useState(false);
  const [bookkeeperOpen, setBookkeeperOpen] = useState(false);
  const [alternativeUsersOpen, setAlternativeUsersOpen] = useState(false);

  // State for form validation and submission
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [duplicateEmailError, setDuplicateEmailError] = useState("");

  // Server-side search for primary contact users
  const searchUsers = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setUsers([]);
      setLoadingUsers(false);
      return;
    }

    setLoadingUsers(true);
    try {
      const response = await axios.get("/api/v2/profiles/", {
        headers: { "Content-Type": "application/json" },
        params: {
          search: searchTerm,
          page_size: 10
        },
        withCredentials: true,
      });

      // Use a Set to ensure unique IDs and avoid duplicate keys
      const uniqueUsers = new Map();

      (response.data.results || []).forEach(profile => {
        if (!uniqueUsers.has(profile.id)) {
          const firstName = profile.user.first_name || '';
          const lastName = profile.user.last_name || '';
          const fullName = `${firstName} ${lastName}`.trim();
          const displayName = fullName || profile.user.email;

          uniqueUsers.set(profile.id, {
            id: profile.id,
            name: displayName,
            email: profile.user.email,
            label: displayName,
            value: profile.user.email,
            firstName: firstName,
            lastName: lastName,
            type: profile.type
          });
        }
      });

      const userOptions = Array.from(uniqueUsers.values());
      setUsers(userOptions);
    } catch (error) {
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, []);


const searchBookkeepers = useCallback(async (searchTerm = "") => {
    setLoadingBookkeepers(true);
    try {
    const params = {};
    if (searchTerm && searchTerm.trim()) {
      params.search = searchTerm.trim();
    }

      const response = await axios.get("/api/v2/profiles/", {
        headers: { "Content-Type": "application/json" },
      params,
        withCredentials: true,
      });

    const bookkeeperProfiles = (response.data.results || []).filter(
      (profile) => profile.type === 'accountant'
      );

    const bookkeeperOptions = bookkeeperProfiles.map((profile) => {
        const firstName = profile.user.first_name || '';
        const lastName = profile.user.last_name || '';
        const fullName = `${firstName} ${lastName}`.trim();
        const displayName = fullName || profile.user.email;

        return {
          id: profile.id,
          name: displayName,
          email: profile.user.email,
          label: displayName,
          value: profile.user.email,
        firstName,
        lastName,
        type: profile.type,
        };
      });

      setBookkeepers(bookkeeperOptions);
    } catch (error) {
      setBookkeepers([]);
    } finally {
      setLoadingBookkeepers(false);
    }
  }, []);

const searchManagers = useCallback(async (searchTerm = "") => {
    setLoadingManagers(true);
    try {
    const params = {};
    if (searchTerm && searchTerm.trim()) {
      params.search = searchTerm.trim();
    }

      const response = await axios.get("/api/v2/profiles/", {
        headers: { "Content-Type": "application/json" },
      params,
        withCredentials: true,
      });

    const managerProfiles = (response.data.results || []).filter(
      (profile) => profile.type === 'manager'
      );

    const managerOptions = managerProfiles.map((profile) => {
        const firstName = profile.user.first_name || '';
        const lastName = profile.user.last_name || '';
        const fullName = `${firstName} ${lastName}`.trim();
        const displayName = fullName || profile.user.email;

        return {
          id: profile.id,
          name: displayName,
          email: profile.user.email,
          label: displayName,
          value: profile.user.email,
        firstName,
        lastName,
        type: profile.type,
        };
      });

      setManagers(managerOptions);
    } catch (error) {
      setManagers([]);
    } finally {
      setLoadingManagers(false);
    }
  }, []);

  // Server-side search for alternative users
  const searchAlternativeUsers = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setAlternativeUsersOptions([]);
      setLoadingAlternativeUsers(false);
      return;
    }

    setLoadingAlternativeUsers(true);
    try {
      const response = await axios.get("/api/v2/profiles/", {
        headers: { "Content-Type": "application/json" },
        params: {
          search: searchTerm,
          page_size: 10,
          external_only: true  // Only fetch external users (excludes Admin, Manager, Bookkeeper, Accountant)
        },
        withCredentials: true,
      });

      const userOptions = (response.data.results || []).map(profile => {
        const firstName = profile.user.first_name || '';
        const lastName = profile.user.last_name || '';
        const fullName = `${firstName} ${lastName}`.trim();
        const displayName = fullName || profile.user.email;

        return {
          id: profile.id,
          optionType: "user",
          name: displayName,
          email: profile.user.email,
          label: displayName,
          value: displayName,
          firstName: firstName,
          lastName: lastName,
          type: profile.type
        };
      });

      if (userOptions.length === 0) {
        setAlternativeUsersOptions([
          {
            id: "__no_user_results__",
            optionType: "noResults",
            searchTerm
          }
        ]);
      } else {
        setAlternativeUsersOptions(userOptions);
      }
    } catch (error) {
      setAlternativeUsersOptions([
        {
          id: "__no_user_results__",
          optionType: "noResults",
          searchTerm
        }
      ]);
    } finally {
      setLoadingAlternativeUsers(false);
    }
  }, []);


  // Validate all required fields
  const validateRequiredFields = useCallback(() => {
    const errors = {};

    if (!formData.accountName.trim()) {
      errors.accountName = "Account Name is required";
    }
    if (!formData.accountType.trim()) {
      errors.accountType = "Account Type is required";
    }
    if (!formData.primaryContact.trim()) {
      errors.primaryContact = "Primary Contact is required";
    }
    if (!formData.primaryContactEmail.trim()) {
      errors.primaryContactEmail = "Primary Contact Email is required";
    }
    if (!formData.manager.trim()) {
      errors.manager = "Manager is required";
    }
    if (!formData.bookkeeper.trim()) {
      errors.bookkeeper = "Bookkeeper is required";
    }

    alternativeUsers.forEach((user, index) => {
      if (user.name.trim() && !user.type.trim()) {
        errors[`altUser_${index}`] = "User Type is required for alternative users";
      }
      if (!user.name.trim() && user.type.trim()) {
        errors[`altUser_${index}`] = "User Name is required when User Type is selected";
      }
    });

    return errors;
  }, [formData, alternativeUsers]);

  // Check for duplicate emails
  const checkDuplicateEmails = useCallback(() => {
    const emails = [];
    let duplicateError = "";

    if (formData.primaryContactEmail.trim()) {
      emails.push(formData.primaryContactEmail.trim().toLowerCase());
    }

    // Add alternative user emails
    alternativeUsers.forEach((user, index) => {

    });

    // Check for duplicates
    const emailCounts = {};
    emails.forEach(email => {
      emailCounts[email] = (emailCounts[email] || 0) + 1;
    });

    const duplicates = Object.keys(emailCounts).filter(email => emailCounts[email] > 1);
    if (duplicates.length > 0) {
      duplicateError = `Duplicate email detected: ${duplicates.join(", ")}`;
    }

    return duplicateError;
  }, [formData, alternativeUsers]);
  const handleSubmit = useCallback(async () => {
    setValidationErrors({});
    setDuplicateEmailError("");

    // Validate required fields
    const fieldErrors = validateRequiredFields();
    if (Object.keys(fieldErrors).length > 0) {
      setValidationErrors(fieldErrors);
      return;
    }

    if (accountNameError || accountNameValidating) {
      setValidationErrors({ accountName: accountNameError || "Please wait for account name validation" });
      return;
    }

    if (primaryContactNotFound) {
      setValidationErrors({ primaryContact: "Primary contact user does not exist" });
      return;
    }

    const duplicateError = checkDuplicateEmails();
    if (duplicateError) {
      setDuplicateEmailError(duplicateError);
      return;
    }

    setIsSubmitting(true);

    try {
      const customerData = {
        name: formData.accountName.trim(),
        email: formData.primaryContactEmail.trim(),
        customer_type: mapFrontendToBackend(formData.accountType),
        frontend_account_type: formData.accountType, // Store the actual frontend selection
        primary_contact: formData.primaryContact.trim(),
        // Optional fields from new creation flow
        manager: formData.manager.trim() || null,
        bookkeeper: formData.bookkeeper.trim() || null,
      };

      const newCustomer = await createCustomer(customerData);

      let successMessage = "Account created successfully!";
      let successSeverity = "success";

      const alternativeUserProfiles = alternativeUsers.filter((user) => user.profileId);
      if (alternativeUserProfiles.length > 0) {
        const profileIds = alternativeUserProfiles.map((user) => user.profileId);
        const profileRoles = alternativeUserProfiles.reduce((acc, user) => {
          acc[user.profileId] = user.type || "Customer";
          return acc;
        }, {});

        try {
          await bulkLinkUsers(newCustomer.data.id, profileIds, [], profileRoles);
        } catch (linkError) {
          successMessage = "Account created, but alternative users could not be linked. Please edit the account and try again.";
          successSeverity = "warning";
        }
      }

      if (onSubmit) {
        onSubmit(newCustomer);
      }

      if (onShowSuccess) {
        onShowSuccess(successMessage, successSeverity);
      }

      onClose();

    } catch (error) {

      if (error.response?.data) {
        const apiErrors = error.response.data;
        if (apiErrors.email) {
          setDuplicateEmailError("This email is already registered in the system");
        } else {
          const newErrors = {};
          Object.keys(apiErrors).forEach(field => {
            newErrors[field] = Array.isArray(apiErrors[field])
              ? apiErrors[field][0]
              : apiErrors[field];
          });
          setValidationErrors(newErrors);
        }
      } else {
        setValidationErrors({
          general: "An error occurred while creating the account. Please try again."
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [
    formData,
    alternativeUsers,
    accountNameError,
    accountNameValidating,
    primaryContactNotFound,
    validateRequiredFields,
    checkDuplicateEmails,
    onSubmit,
    onShowSuccess,
    onClose
  ]);

  // Validate account name against existing customers
  const validateAccountName = useCallback(async (accountName) => {
    if (!accountName || accountName.trim().length < 2) {
      setAccountNameError("");
      setAccountNameValidating(false);
      return;
    }

    setAccountNameValidating(true);
    setAccountNameError("");

    try {
      const response = await fetchCustomers(profile, {
        search: accountName.trim(),
        page_size: 100
      });

      // Check for exact matches (case-insensitive)
      const exactMatch = response.results?.find(customer =>
        customer.name.toLowerCase() === accountName.toLowerCase().trim()
      );

      if (exactMatch) {
        setAccountNameError(`Business "${accountName}" already exists in the system`);
      } else {
        setAccountNameError("");
      }
    } catch (error) {
      setAccountNameError("");
    } finally {
      setAccountNameValidating(false);
    }
  }, [profile]);

  // Debounced account name validation
  const debouncedAccountNameValidation = useCallback((accountName) => {

    if (accountNameDebounceTimer) {
      clearTimeout(accountNameDebounceTimer);
    }

    const newTimer = setTimeout(() => {
      validateAccountName(accountName);
    }, 500);

    setAccountNameDebounceTimer(newTimer);
  }, [validateAccountName, accountNameDebounceTimer]);

  useEffect(() => {
    return () => {
      if (accountNameDebounceTimer) {
        clearTimeout(accountNameDebounceTimer);
      }
    };
  }, [accountNameDebounceTimer]);

// Prefetch initial manager and bookkeeper lists
useEffect(() => {
  searchManagers();
  searchBookkeepers();
}, [searchManagers, searchBookkeepers]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Trigger account name validation when account name changes
    if (field === "accountName") {
      setAccountNameError("");

      if (value && value.trim().length >= 2) {
        debouncedAccountNameValidation(value);
      } else {
        setAccountNameValidating(false);
        setAccountNameError("");
      }
    }
  };


  const handlePrimaryContactBlur = () => {
    const currentValue = formData.primaryContact;
    if (currentValue && currentValue.length > 0 && !selectedUser) {
      const matchedUser = users.find(user =>
        user.name.toLowerCase() === currentValue.toLowerCase() ||
        user.email.toLowerCase() === currentValue.toLowerCase()
      );

      if (!matchedUser) {
        setPrimaryContactNotFound(true);
      } else {
        setPrimaryContactNotFound(false);
        setSelectedUser(matchedUser);
        setFormData(prev => ({
          ...prev,
          primaryContact: matchedUser.name,
          primaryContactEmail: matchedUser.email
        }));
      }
    }
  };


  const handleAddUser = () => {
    if (selectedAlternativeUser?.id && newUserName && newUserType) {
      // Map "Accountant" to "External Accountant" for backend
      const mappedType = newUserType === "Accountant" ? "External Accountant" : newUserType;
      const updatedUsers = [...alternativeUsers, {
        name: newUserName,
        type: mappedType,
        profileId: selectedAlternativeUser.id,
        profileType: selectedAlternativeUser?.type // Store the user's actual profile type for role filtering
      }];
      setAlternativeUsers(updatedUsers);
      setNewUserName("");
      setNewUserType("");
      setSelectedAlternativeUser(null);
      setAlternativeUsersOptions([]);
    }
  };

  const handleDeleteUser = (index) => {
    setAlternativeUsers(alternativeUsers.filter((_, i) => i !== index));
  };

  const handleCreateNewUser = () => {
    onClose();
    navigate('/management/users');
    setTimeout(() => {
      const event = new CustomEvent('openUserCreateDialog');
      window.dispatchEvent(event);
    }, 100);
  };

  // Check if form is valid
  const isFormValid = () => {
    const requiredFields = [
      formData.accountName,
      formData.accountType,
      formData.primaryContact,
      formData.primaryContactEmail,
      formData.manager,
      formData.bookkeeper
    ];

    const allRequiredFieldsFilled = requiredFields.every(field => field && field.trim() !== "");

    // Check if alternative users have both name and type when added
    const alternativeUsersValid = alternativeUsers.every(user => user.name && user.type);

    // Check if currently adding user has both fields if one is filled
    const currentUserValid = (!newUserName && !newUserType) || (newUserName && newUserType);

    // Check if primary contact exists in system
    const primaryContactValid = !primaryContactNotFound && selectedUser;

    // Check account name validation
    const accountNameValid = !accountNameError && !accountNameValidating && formData.accountName.trim().length >= 2;

    return allRequiredFieldsFilled && alternativeUsersValid && currentUserValid && primaryContactValid && accountNameValid;
  };


  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
      sx={{
        zIndex: 9999,
        '& .MuiAutocomplete-popper': {
          zIndex: 999999,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Create Account
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Here, you can view account details, track connections, and edit
            essential.
          </Typography>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Primary Fields
            </Typography>

            <Grid container rowSpacing={3} columnSpacing={2}>
              <Grid size={6}>
                <TextField
                  label="Account Name"
                  value={formData.accountName}
                  onChange={handleChange("accountName")}
                  required
                  fullWidth
                  size="small"
                  error={!!accountNameError || !!validationErrors.accountName}
                  helperText={
                    validationErrors.accountName ||
                    accountNameError
                  }
                  InputProps={{
                    endAdornment: accountNameValidating ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', pr: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Checking...
                        </Typography>
                      </Box>
                    ) : null
                  }}
                />
              </Grid>
              <Grid size={6}>
                <FormControl
                  fullWidth
                  variant="outlined"
                  size="small"
                  error={!!validationErrors.accountType}
                >
                  <InputLabel id="account-type-label">Account Type</InputLabel>
                  <Select
                    labelId="account-type-label"
                    id="account-type-select"
                    value={formData.accountType}
                    onChange={handleChange("accountType")}
                    label="Account Type"
                    required
                    MenuProps={{
                      disablePortal: true,
                      PaperProps: {
                        style: {
                          zIndex: 99999,
                        },
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>Select Account Type</em>
                    </MenuItem>
                    <MenuItem value="Trial Demo">Trial Demo</MenuItem>
                    <MenuItem value="Unpublished">Unpublished</MenuItem>
                    <MenuItem value="Published">Published</MenuItem>
                    <MenuItem value="Platform">Platform</MenuItem>
                  </Select>
                  {validationErrors.accountType && (
                    <FormHelperText>{validationErrors.accountType}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid size={6}>
                <Autocomplete
                  options={users}
                  value={selectedUser}
                  loading={loadingUsers}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return option.name || option.email || '';
                  }}
                  isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return false;
                    return option.id === value.id;
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === 'input') {
                      setFormData(prev => ({ ...prev, primaryContact: newInputValue }));
                      setSelectedUser(null);
                      setFormData(prev => ({ ...prev, primaryContactEmail: "" }));
                      setPrimaryContactNotFound(false);

                      if (!newInputValue || newInputValue.length < 2) {
                        setUsers([]);
                        setLoadingUsers(false);
                        setPrimaryContactOpen(false);
                      } else {
                        setPrimaryContactOpen(true);
                        searchUsers(newInputValue);
                      }
                    }
                  }}
                  onChange={(event, newValue, reason) => {
                    if (newValue && typeof newValue === 'object') {
                      setSelectedUser(newValue);
                      setPrimaryContactNotFound(false);
                      setFormData(prev => ({
                        ...prev,
                        primaryContact: newValue.name,
                        primaryContactEmail: newValue.email
                      }));
                      setUsers([]);
                      setPrimaryContactOpen(false);
                    } else if (newValue === null || newValue === '') {
                      setSelectedUser(null);
                      setPrimaryContactNotFound(false);
                      setFormData(prev => ({ ...prev, primaryContact: "", primaryContactEmail: "" }));
                      setUsers([]);
                      setPrimaryContactOpen(false);
                    }
                  }}
                  filterOptions={(options) => {
                    return options;
                  }}
                  open={primaryContactOpen}
                  onOpen={() => {
                    if (formData.primaryContact && formData.primaryContact.length >= 2) {
                      setPrimaryContactOpen(true);
                    }
                  }}
                  onClose={(event, reason) => {
                    // Prevent auto-close, only close on blur
                  }}
                  selectOnFocus
                  clearOnBlur={false}
                  handleHomeEndKeys
                  onBlur={() => {
                    setUsers([]);
                    setLoadingUsers(false);
                    setPrimaryContactOpen(false);
                  }}
                  loadingText="Searching users..."
                  noOptionsText={
                    formData.primaryContact && formData.primaryContact.length >= 2
                      ? <Box>
                          <Typography variant="body2" sx={{ px: 2, py: 1 }}>
                            No existing user matches "<em>{formData.primaryContact}</em>"
                          </Typography>
                          <Divider />
                          <Box
                            sx={{ px: 2, py: 1, cursor: 'pointer' }}
                            onClick={handleCreateNewUser}
                          >
                            <Typography variant="body2" sx={{ color: '#627CFB', fontWeight: 500 }}>
                              + Create New User
                            </Typography>
                          </Box>
                        </Box>
                      : "Type at least 2 characters to search..."
                  }
                  renderInput={(params) => (
                <TextField
                      {...params}
                  label="Primary Contact"
                      size="small"
                  fullWidth
                      variant="outlined"
                      required
                      error={primaryContactNotFound || !!validationErrors.primaryContact}
                      helperText={validationErrors.primaryContact}
                      onBlur={handlePrimaryContactBlur}
                    />
                  )}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    const uniqueKey = `user-${option.id}`;
                    return (
                      <li key={uniqueKey} {...otherProps}>
                        <div>
                          <div style={{ fontWeight: 'bold' }}>
                            {option.firstName || option.lastName
                              ? `${option.firstName} ${option.lastName}`.trim()
                              : option.email
                            }
                          </div>
                          <div style={{ fontSize: '0.875rem', color: '#666' }}>
                            {option.email} • {option.type}
                          </div>
                        </div>
                      </li>
                    );
                  }}
                  slotProps={{
                    popper: {
                      placement: 'bottom-start',
                      style: {
                        zIndex: 999999,
                        position: 'fixed'
                      },
                      modifiers: [
                        {
                          name: 'flip',
                          enabled: false,
                        },
                        {
                          name: 'preventOverflow',
                          enabled: false,
                        },
                      ],
                    },
                  }}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="Primary Contact Email"
                  type="email"
                  value={formData.primaryContactEmail}
                  disabled
                  required
                  fullWidth
                  size="small"
                />
              </Grid>

              <Grid size={6}>
                <Autocomplete
                  options={managers}
                  value={selectedManager}
                  loading={loadingManagers}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return option.name || option.email || '';
                  }}
                  isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return false;
                    return option.id === value.id;
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === 'input') {
                      setFormData(prev => ({ ...prev, manager: newInputValue || "" }));
                      setSelectedManager(null);
                      searchManagers(newInputValue || "");
                    } else if (reason === 'clear') {
                      setFormData(prev => ({ ...prev, manager: "" }));
                      setSelectedManager(null);
                      searchManagers("");
                    }
                  }}
                  onChange={(event, newValue, reason) => {
                    if (newValue && typeof newValue === 'object') {
                      setSelectedManager(newValue);
                      setFormData(prev => ({
                        ...prev,
                        manager: newValue.name
                      }));
                    } else if (newValue === null || newValue === '') {
                      setSelectedManager(null);
                      setFormData(prev => ({ ...prev, manager: "" }));
                    }
                  }}
                  filterOptions={(options) => options}
                  open={managerOpen}
                  onOpen={() => {
                      setManagerOpen(true);
                    if (managers.length === 0 && !loadingManagers) {
                      searchManagers(formData.manager || "");
                    }
                  }}
                  onClose={() => {
                    setManagerOpen(false);
                  }}
                  selectOnFocus
                  clearOnBlur={false}
                  handleHomeEndKeys
                  loadingText="Loading managers..."
                  noOptionsText={
                    (formData.manager && formData.manager.trim().length > 0)
                      ? <Box>
                          <Typography variant="body2" sx={{ px: 2, py: 1 }}>
                            No existing user matches "<em>{formData.manager}</em>"
                          </Typography>
                          <Divider />
                          <Box
                            sx={{ px: 2, py: 1, cursor: 'pointer' }}
                            onClick={handleCreateNewUser}
                          >
                            <Typography variant="body2" sx={{ color: '#627CFB', fontWeight: 500 }}>
                              + Create New User
                            </Typography>
                          </Box>
                        </Box>
                      : "Type to search managers..."
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Manager"
                      size="small"
                      fullWidth
                      variant="outlined"
                      required
                    />
                  )}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    const uniqueKey = `manager-${option.id}`;
                    return (
                      <li key={uniqueKey} {...otherProps}>
                        <div>
                          <div style={{ fontWeight: 'bold' }}>
                            {option.firstName || option.lastName
                              ? `${option.firstName} ${option.lastName}`.trim()
                              : option.email
                            }
                          </div>
                          <div style={{ fontSize: '0.875rem', color: '#666' }}>
                            {option.email} • {option.type}
                          </div>
                        </div>
                      </li>
                    );
                  }}
                  slotProps={{
                    popper: {
                      placement: 'bottom-start',
                      style: {
                        zIndex: 999999,
                        position: 'fixed'
                      },
                      modifiers: [
                        {
                          name: 'flip',
                          enabled: false,
                        },
                        {
                          name: 'preventOverflow',
                          enabled: false,
                        },
                      ],
                    },
                  }}
                  ListboxProps={{
                    style: {
                      maxHeight: '200px',
                      overflow: 'auto'
                    }
                  }}
                />
              </Grid>
              <Grid size={6}>
                <Autocomplete
                  options={bookkeepers}
                  value={selectedBookkeeper}
                  loading={loadingBookkeepers}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return option.name || option.email || '';
                  }}
                  isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return false;
                    return option.id === value.id;
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === 'input') {
                      setFormData(prev => ({ ...prev, bookkeeper: newInputValue || "" }));
                      setSelectedBookkeeper(null);
                      searchBookkeepers(newInputValue || "");
                    } else if (reason === 'clear') {
                      setFormData(prev => ({ ...prev, bookkeeper: "" }));
                      setSelectedBookkeeper(null);
                      searchBookkeepers("");
                    }
                  }}
                  onChange={(event, newValue, reason) => {
                    if (newValue && typeof newValue === 'object') {
                      setSelectedBookkeeper(newValue);
                      setFormData(prev => ({
                        ...prev,
                        bookkeeper: newValue.name
                      }));
                    } else if (newValue === null || newValue === '') {
                      setSelectedBookkeeper(null);
                      setFormData(prev => ({ ...prev, bookkeeper: "" }));
                    }
                  }}
                  filterOptions={(options) => options}
                  open={bookkeeperOpen}
                  onOpen={() => {
                      setBookkeeperOpen(true);
                    if (bookkeepers.length === 0 && !loadingBookkeepers) {
                      searchBookkeepers(formData.bookkeeper || "");
                    }
                  }}
                  onClose={() => {
                    setBookkeeperOpen(false);
                  }}
                  selectOnFocus
                  clearOnBlur={false}
                  handleHomeEndKeys
                  loadingText="Loading accountants..."
                  noOptionsText={
                    (formData.bookkeeper && formData.bookkeeper.trim().length > 0)
                      ? <Box>
                          <Typography variant="body2" sx={{ px: 2, py: 1 }}>
                            No existing user matches "<em>{formData.bookkeeper}</em>"
                          </Typography>
                          <Divider />
                          <Box
                            sx={{ px: 2, py: 1, cursor: 'pointer' }}
                            onClick={handleCreateNewUser}
                          >
                            <Typography variant="body2" sx={{ color: '#627CFB', fontWeight: 500 }}>
                              + Create New User
                            </Typography>
                          </Box>
                        </Box>
                      : "Type to search accountants..."
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                    label="Bookkeeper"
                      size="small"
                      fullWidth
                      variant="outlined"
                      required
                    />
                  )}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    const uniqueKey = `bookkeeper-${option.id}`;
                    return (
                      <li key={uniqueKey} {...otherProps}>
                        <div>
                          <div style={{ fontWeight: 'bold' }}>
                            {option.firstName || option.lastName
                              ? `${option.firstName} ${option.lastName}`.trim()
                              : option.email
                            }
                          </div>
                          <div style={{ fontSize: '0.875rem', color: '#666' }}>
                            {option.email} • {option.type}
                          </div>
                        </div>
                      </li>
                    );
                  }}
                  slotProps={{
                    popper: {
                      placement: 'bottom-start',
                      style: {
                        zIndex: 999999,
                        position: 'fixed'
                      },
                      modifiers: [
                        {
                          name: 'flip',
                          enabled: false,
                        },
                        {
                          name: 'preventOverflow',
                          enabled: false,
                        },
                      ],
                    },
                  }}
                  ListboxProps={{
                    style: {
                      maxHeight: '200px',
                      overflow: 'auto'
                    }
                  }}
                />
              </Grid>
             </Grid>
           </Box>

           <Box>
             <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
               Alternative Users
             </Typography>

             {/* Display existing alternative users */}
             {alternativeUsers.map((user, index) => {
               // Get allowed roles for this user's profile type
               const allowedRoles = user.profileType ? getAllowedAccountRoles(user.profileType) : ["Lead", "Customer", "Viewer", "Accountant"];

               return (
               <Grid container columnSpacing={2} key={index} sx={{ mb: 2 }}>
                 <Grid size={6}>
                   <TextField
                     label="User Name"
                     value={user.name}
                     disabled
                     fullWidth
                     size="small"
                   />
                 </Grid>
                <Grid size={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <FormControl fullWidth variant="outlined" size="small">
                      <InputLabel>User Role on Account</InputLabel>
                      <Select
                        value={user.type}
                        disabled
                        label="User Role on Account"
                      >
                        {allowedRoles.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteUser(index)}
                      sx={{ color: "#ef4444" }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
               </Grid>
               );
             })}

             {/* Add new user row */}
             <Grid container columnSpacing={2} sx={{ mb: 2 }}>
               <Grid size={6}>
               <Autocomplete
                  freeSolo
                  options={alternativeUsersOptions}
                  value={newUserName ? alternativeUsersOptions.find(user => user.name === newUserName) || newUserName : null}
                  inputValue={newUserName}
                  loading={loadingAlternativeUsers}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    if (option.optionType === 'noResults') return option.searchTerm || '';
                    return option.name || option.email || '';
                  }}
                  isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return false;
                    return option.id === value.id;
                  }}
                  onInputChange={(event, newValue, reason) => {
                    if (reason === 'input') {
                      setNewUserName(newValue || "");
                      setSelectedAlternativeUser(null);
                      setNewUserType("");
                      if (newValue && newValue.length >= 2) {
                        setAlternativeUsersOpen(true);
                        searchAlternativeUsers(newValue);
                      } else {
                        setAlternativeUsersOptions([]);
                        setAlternativeUsersOpen(false);
                      }
                    } else if (reason === 'clear') {
                      setNewUserName("");
                      setNewUserType("");
                      setSelectedAlternativeUser(null);
                      setAlternativeUsersOptions([]);
                      setAlternativeUsersOpen(false);
                    }
                  }}
                  onChange={(event, newValue, reason) => {
                    if (reason === 'selectOption' && newValue && typeof newValue === 'object') {
                      if (newValue.optionType === 'noResults') {
                        handleCreateNewUser();
                        setAlternativeUsersOptions([]);
                        setNewUserName("");
                        setNewUserType("");
                        setSelectedAlternativeUser(null);
                        setAlternativeUsersOpen(false);
                        return;
                      }
                      setNewUserName(newValue.name);
                      setSelectedAlternativeUser(newValue); // Store selected user for role filtering
                      setAlternativeUsersOptions([]);
                      setAlternativeUsersOpen(false);
                    } else if (reason === 'clear' || newValue === null || newValue === '') {
                      setNewUserName("");
                      setNewUserType("");
                      setSelectedAlternativeUser(null);
                      setAlternativeUsersOptions([]);
                      setAlternativeUsersOpen(false);
                    }
                  }}
                  filterOptions={(options) => options}
                  open={alternativeUsersOpen}
                  onOpen={() => {
                    if (newUserName && newUserName.length >= 2) {
                      setAlternativeUsersOpen(true);
                    }
                  }}
                  onClose={() => {
                    // prevent MUI from closing automatically; state drives visibility
                  }}
                  selectOnFocus
                  clearOnBlur={false}
                  handleHomeEndKeys
                  onBlur={() => {
                    setAlternativeUsersOptions([]);
                    setAlternativeUsersOpen(false);
                  }}
                  loadingText="Searching users..."
                  noOptionsText="Type at least 2 characters to search users..."
                   renderInput={(params) => (
                     <TextField
                       {...params}
                       label="User Name"
                       size="small"
                       fullWidth
                     />
                   )}
                   renderOption={(props, option) => {
                     if (option.optionType === 'noResults') {
                       return (
                         <li {...props}>
                           <Box sx={{ width: '100%' }}>
                             <Typography variant="body2" sx={{ px: 2, py: 1 }}>
                               No existing user matches "<em>{option.searchTerm}</em>"
                             </Typography>
                             <Divider />
                             <Box
                               sx={{ px: 2, py: 1, cursor: 'pointer' }}
                               onMouseDown={(event) => event.preventDefault()}
                               onClick={() => {
                                 handleCreateNewUser();
                                 setAlternativeUsersOptions([]);
                               }}
                             >
                               <Typography variant="body2" sx={{ color: '#627CFB', fontWeight: 500 }}>
                                 + Create New User
                               </Typography>
                             </Box>
                           </Box>
                         </li>
                       );
                     }

                     return (
                       <li {...props}>
                         <div>
                           <div style={{ fontWeight: 'bold' }}>
                             {option.firstName || option.lastName
                               ? `${option.firstName} ${option.lastName}`.trim()
                               : option.email
                             }
                           </div>
                           <div style={{ fontSize: '0.875rem', color: '#666' }}>
                             {option.email} • {option.type}
                           </div>
                         </div>
                       </li>
                     );
                   }}
                   slotProps={{
                     popper: {
                       placement: 'bottom-start',
                       style: {
                         zIndex: 999999,
                         position: 'fixed'
                       },
                       modifiers: [
                         {
                           name: 'flip',
                           enabled: false,
                         },
                         {
                           name: 'preventOverflow',
                           enabled: false,
                         },
                       ],
                     },
                   }}
                 />
               </Grid>
              <Grid size={6}>
                <FormControl fullWidth size="small" required={!!newUserName}>
                  <InputLabel>User Role on Account</InputLabel>
                  <Select
                    value={newUserType}
                    onChange={(event) => setNewUserType(event.target.value)}
                    label="User Role on Account"
                    MenuProps={{
                      sx: { zIndex: 99999 }
                    }}
                  >
                    {(selectedAlternativeUser?.type
                      ? getAllowedAccountRoles(selectedAlternativeUser.type)
                      : ["Lead", "Customer", "Viewer", "Accountant"]
                    ).map((role) => (
                      <MenuItem key={role} value={role}>{role}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
             </Grid>

             <Button
               startIcon={<AddIcon />}
               onClick={handleAddUser}
               disabled={!selectedAlternativeUser || !newUserName || !newUserType}
               sx={{
                 color: "#627CFB !important",
                 textTransform: "none",
                 fontWeight: 500,
                 border: (selectedAlternativeUser && newUserName && newUserType) ? "1.5px solid #627CFB" : "1.5px solid transparent",
                 transition: "all 0.2s ease-in-out",
                 "& .MuiButton-startIcon": {
                   color: "#627CFB !important",
                 },
                 "&:hover": {
                   backgroundColor: "rgba(98, 124, 251, 0.04)",
                   border: (selectedAlternativeUser && newUserName && newUserType) ? "1.5px solid #627CFB" : "1.5px solid transparent",
                 },
               }}
             >
               Add User
             </Button>
           </Box>

         </Stack>
       </DialogContent>

      {(duplicateEmailError || validationErrors.general) && (
        <Box sx={{ px: 3, pb: 2 }}>
          <Typography
            variant="body2"
            color="error"
            sx={{
              backgroundColor: "rgba(244, 67, 54, 0.1)",
              padding: 2,
              borderRadius: 1,
              border: "1px solid rgba(244, 67, 54, 0.3)"
            }}
          >
            {duplicateEmailError || validationErrors.general}
          </Typography>
        </Box>
      )}

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isFormValid() || isSubmitting}
          sx={{
            backgroundColor: "#627CFB",
            "&:hover": { backgroundColor: "#536DEC" },
          }}
        >
          {isSubmitting ? "Creating..." : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
